import { Heading } from '@/components/Heading';
import { Pill } from '@/components/Pill';
import { SPACE } from '@/design/tokens';
import type { ReactNode } from 'react';

type HeadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface PageHeaderProps {
  title: string;
  titleSize?: HeadingSize;
  /** When true, renders an adjacent "Syncing" pill in orange. */
  syncing?: boolean;
  /** Optional right-aligned action slot (buttons, pills, etc.). */
  actions?: ReactNode;
}

/**
 * PageHeader — standard title zone for flat pages.
 *
 * Renders an h1 heading, an optional "Syncing" pill when offline writes are
 * queued, and an optional right-aligned `actions` slot. Mirrors the existing
 * layout in ServiceLog and ProjectsPage exactly.
 *
 * Exception: VehicleDetail's title lives inside its ruled dossier spec-block
 * and does NOT use PageHeader. The PageHeader is for flat, non-record pages.
 */
export function PageHeader({ title, titleSize = 'xl', syncing, actions }: PageHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginTop: SPACE.sm,
        gap: SPACE.md,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.sm }}>
        <Heading level={1} size={titleSize}>
          {title}
        </Heading>
        {syncing ? <Pill color="orange">Syncing</Pill> : null}
      </div>
      {actions ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>{actions}</div>
      ) : null}
    </div>
  );
}
